import React, { useState, useEffect } from 'react';
import {
  Card,
  Row,
  Col,
  Select,
  Button,
  Table,
  Typography,
  message,
  Space,
  Alert,
  Checkbox,
  Tag
} from 'antd';
import type { CheckboxChangeEvent } from 'antd/es/checkbox';
import { FileExcelOutlined, SearchOutlined } from '@ant-design/icons';
import { customerAPI, reportAPI } from '../services/api.ts';

const { Title, Text } = Typography;
const { Option } = Select;

interface Customer {
  id: number;
  company: string;
}

interface SituationRow {
  product_id: number;
  client_product_code?: string;
  internal_product_code?: string;
  product_description?: string;
  is_associated: boolean;
  dedicated_available: number;
  dedicated_in_production: number;
  dedicated_in_transit: number;
  free_available: number;
  reserved_available: number;
  reserved_in_production: number;
  reserved_in_transit: number;
  stock_order_requested: number;
  stock_order_loaded: number;
  stock_order_missing: number;
}

/**
 * Report Situazione Cliente: per ogni prodotto del cliente incrocia giacenza
 * DEDICATA (product_stocks.customer_id), merce PRENOTATA dai suoi ordini aperti,
 * copertura dei suoi ORDINI STOCK aperti e disponibilità LIBERA (senza cliente).
 * Vista a video + export Excel con le stesse righe.
 */
const ReportSituazioneCliente: React.FC = () => {
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [selectedCustomerId, setSelectedCustomerId] = useState<number | undefined>(undefined);
  const [onlyWithData, setOnlyWithData] = useState<boolean>(true);
  const [loadingOptions, setLoadingOptions] = useState(false);
  const [loading, setLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [rows, setRows] = useState<SituationRow[]>([]);
  const [reportCustomer, setReportCustomer] = useState<string | null>(null);

  useEffect(() => {
    const loadOptions = async () => {
      setLoadingOptions(true);
      try {
        const customersData = await customerAPI.getCustomers();
        setCustomers(customersData);
      } catch (error) {
        console.error('Errore caricamento clienti:', error);
        message.error('Errore nel caricamento dei clienti');
      } finally {
        setLoadingOptions(false);
      }
    };
    loadOptions();
  }, []);

  const handleView = async () => {
    if (!selectedCustomerId) {
      message.warning('Seleziona un cliente');
      return;
    }
    setLoading(true);
    try {
      const data = await reportAPI.getSituazioneCliente(selectedCustomerId, onlyWithData);
      setRows(data.rows || []);
      setReportCustomer(data.customer?.company || null);
      if (!data.rows || data.rows.length === 0) {
        message.info('Nessun dato per il cliente selezionato');
      }
    } catch (error: any) {
      console.error('Errore caricamento report:', error);
      message.error(error?.response?.data?.detail || 'Errore nel caricamento del report');
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async () => {
    if (!selectedCustomerId) {
      message.warning('Seleziona un cliente');
      return;
    }
    setExportLoading(true);
    try {
      await reportAPI.exportSituazioneClienteExcel(selectedCustomerId, onlyWithData);
      message.success('Report Excel generato con successo');
    } catch (error: any) {
      console.error('Errore generazione report:', error);
      message.error(error?.response?.data?.detail || 'Errore durante la generazione del report');
    } finally {
      setExportLoading(false);
    }
  };

  const num = (v: number, strong = false) => (
    v ? <Text strong={strong}>{v.toLocaleString('it-IT')}</Text> : <Text type="secondary">0</Text>
  );

  const columns = [
    {
      title: 'Prodotto',
      key: 'product',
      fixed: 'left' as const,
      width: 280,
      render: (r: SituationRow) => (
        <div>
          <Text strong>{r.internal_product_code}</Text>
          {r.client_product_code && <Text type="secondary"> · {r.client_product_code}</Text>}
          <br />
          <Text type="secondary" style={{ fontSize: 12 }}>{r.product_description}</Text>
        </div>
      ),
    },
    {
      title: 'Giacenza dedicata al cliente',
      children: [
        { title: 'Disponibile', key: 'da', width: 100, align: 'right' as const, render: (r: SituationRow) => num(r.dedicated_available, true) },
        { title: 'In produzione', key: 'dp', width: 105, align: 'right' as const, render: (r: SituationRow) => num(r.dedicated_in_production) },
        { title: 'In transito', key: 'dt', width: 95, align: 'right' as const, render: (r: SituationRow) => num(r.dedicated_in_transit) },
      ],
    },
    {
      title: 'Libera',
      children: [
        { title: 'Disponibile', key: 'fa', width: 95, align: 'right' as const, render: (r: SituationRow) => num(r.free_available) },
      ],
    },
    {
      title: 'Prenotato dai suoi ordini',
      children: [
        { title: 'Da disponibile', key: 'ra', width: 110, align: 'right' as const, render: (r: SituationRow) => num(r.reserved_available) },
        { title: 'Da produzione', key: 'rp', width: 110, align: 'right' as const, render: (r: SituationRow) => num(r.reserved_in_production) },
        { title: 'Da transito', key: 'rt', width: 95, align: 'right' as const, render: (r: SituationRow) => num(r.reserved_in_transit) },
      ],
    },
    {
      title: 'Ordini stock aperti',
      children: [
        { title: 'Richiesto', key: 'sr', width: 90, align: 'right' as const, render: (r: SituationRow) => num(r.stock_order_requested) },
        { title: 'Entrato', key: 'sl', width: 85, align: 'right' as const, render: (r: SituationRow) => num(r.stock_order_loaded) },
        {
          title: 'Mancante', key: 'sm', width: 95, align: 'right' as const,
          render: (r: SituationRow) => r.stock_order_missing > 0
            ? <Text strong type="danger">{r.stock_order_missing.toLocaleString('it-IT')}</Text>
            : <Text type="secondary">0</Text>
        },
      ],
    },
    {
      title: 'Associato',
      key: 'assoc',
      width: 95,
      align: 'center' as const,
      render: (r: SituationRow) => r.is_associated
        ? <Tag color="green">SI</Tag>
        : <Tag color="orange">NO</Tag>,
    },
  ];

  return (
    <div style={{ padding: '24px' }}>
      <Card>
        <Title level={2} style={{ marginBottom: '24px' }}>Report Situazione Cliente</Title>

        <Alert
          message="Per ogni prodotto del cliente: giacenza DEDICATA (intestata al cliente in magazzino), disponibilità LIBERA (giacenze senza cliente), merce PRENOTATA dai suoi ordini aperti e stato di copertura dei suoi ORDINI STOCK aperti (richiesto / entrato / mancante). 'Associato' indica la presenza dell'associazione anagrafica prodotto-cliente."
          type="info"
          showIcon
          style={{ marginBottom: '24px' }}
        />

        <Row gutter={[16, 16]} align="bottom">
          <Col xs={24} sm={12} md={8}>
            <div style={{ marginBottom: '4px', fontWeight: 500 }}>Cliente</div>
            <Select
              placeholder="Seleziona cliente"
              showSearch
              style={{ width: '100%' }}
              value={selectedCustomerId}
              onChange={(value) => setSelectedCustomerId(value)}
              filterOption={(input, option) =>
                (option?.children as unknown as string)?.toLowerCase().includes(input.toLowerCase())
              }
              loading={loadingOptions}
            >
              {customers.map(customer => (
                <Option key={customer.id} value={customer.id}>
                  {customer.company}
                </Option>
              ))}
            </Select>
          </Col>

          <Col xs={24} sm={12} md={10}>
            <Space wrap>
              <Button
                type="primary"
                icon={<SearchOutlined />}
                onClick={handleView}
                loading={loading}
                size="large"
              >
                Visualizza
              </Button>
              <Button
                icon={<FileExcelOutlined />}
                onClick={handleExport}
                loading={exportLoading}
                size="large"
              >
                Esporta Excel
              </Button>
            </Space>
          </Col>
        </Row>

        <Row style={{ marginTop: '16px', marginBottom: '16px' }}>
          <Col span={24}>
            <Checkbox
              checked={onlyWithData}
              onChange={(e: CheckboxChangeEvent) => setOnlyWithData(e.target.checked)}
            >
              Escludi i prodotti con tutte le quantità a zero
            </Checkbox>
          </Col>
        </Row>

        {reportCustomer && (
          <div style={{ marginBottom: 8 }}>
            <Text strong>Situazione di: </Text>
            <Tag color="blue">{reportCustomer}</Tag>
            <Text type="secondary">({rows.length} prodotti)</Text>
          </div>
        )}

        <Table
          columns={columns}
          dataSource={rows}
          rowKey="product_id"
          size="small"
          loading={loading}
          scroll={{ x: 1300 }}
          pagination={{
            pageSize: 50,
            showSizeChanger: true,
            showTotal: (total, range) => `${range[0]}-${range[1]} di ${total} prodotti`,
            pageSizeOptions: ['50', '100', '200'],
          }}
          locale={{ emptyText: 'Seleziona un cliente e premi Visualizza' }}
        />
      </Card>
    </div>
  );
};

export default ReportSituazioneCliente;
